import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Dumbbell, Plus } from 'lucide-react';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Combobox, EmptyState, Input, MultiSelect, Select, Skeleton, Textarea, Toggle, useConfirm } from '@techsofit/ui';
import { FormShell } from '../components/FormShell';
import { ImageUpload } from '../components/ImageUpload';
import { DAYS_PER_WEEK, EQUIPMENT, GOALS, LEVELS, TARGET_AREAS } from '../lib/programOptions';
import { useAsync } from '../lib/useAsync';
import {
  createProgram,
  deleteProgram,
  getPrograms,
  updateProgram,
  type AdminProgram,
  type ProgramAudience,
  type ProgramType,
} from '../lib/adminApi';

const AUDIENCE: ProgramAudience[] = ['EVERYONE', 'WOMEN', 'MEN'];
const TYPES: ProgramType[] = ['PROGRAM', 'CHALLENGE'];

export function ProgramsPage() {
  const { data, loading, error, reload } = useAsync(getPrograms);
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const list = data ?? [];

  // After creating a program, jump straight to its workouts — that's the next step.
  function afterCreate(created?: AdminProgram) {
    setAdding(false);
    if (created) navigate(`/programs/${created.id}/workouts`);
    else reload();
  }

  // Focused create/edit view — the list is hidden so it doesn't read as "already added".
  if (adding) {
    return (
      <FormShell title="New program" onBack={() => setAdding(false)}>
        <ProgramForm mode="create" onCancel={() => setAdding(false)} onDone={afterCreate} />
      </FormShell>
    );
  }
  const editing = list.find((p) => p.id === editingId);
  if (editing) {
    return (
      <FormShell title="Edit program" onBack={() => setEditingId(null)}>
        <ProgramForm mode="edit" program={editing} onCancel={() => setEditingId(null)} onDone={() => { setEditingId(null); reload(); }} />
      </FormShell>
    );
  }

  return (
    <section className="mt-6 animate-fade-up">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-display-md text-ink">Programs & challenges</h1>
          <p className="mt-1 text-ink-soft">Create a program, then add its weekly workouts.</p>
        </div>
        <Button onClick={() => setAdding(true)}>
          <Plus className="size-4" aria-hidden /> New program
        </Button>
      </div>

      {loading ? (
        <div className="mt-4 grid gap-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : error ? (
        <EmptyState title="Couldn't load programs" description={error} />
      ) : list.length === 0 ? (
        <div className="mt-4">
          <EmptyState title="No programs yet" description="Create your first program for the app." />
          <div className="mt-3 flex justify-center">
            <Button onClick={() => setAdding(true)}>
              <Plus className="size-4" aria-hidden /> New program
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {list.map((p) => (
            <ProgramCard key={p.id} p={p} onEdit={() => setEditingId(p.id)} onDeleted={reload} />
          ))}
        </div>
      )}
    </section>
  );
}

function ProgramCard({ p, onEdit, onDeleted }: { p: AdminProgram; onEdit: () => void; onDeleted: () => void }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  async function remove() {
    if (!(await confirm({ title: `Delete "${p.title}"?`, description: 'Its workouts are removed too. This cannot be undone.', confirmLabel: 'Delete', tone: 'danger' }))) return;
    setBusy(true);
    try {
      await deleteProgram(p.id);
      onDeleted();
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card className="flex flex-wrap items-center gap-3 p-4">
      {p.coverImage ? (
        <img src={p.coverImage} alt="" className="size-12 shrink-0 rounded-control object-cover" />
      ) : (
        <span className="grid size-12 shrink-0 place-items-center rounded-control bg-surface text-muted"><Dumbbell className="size-5" /></span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-ink">{p.title}</p>
          <Tag>{p.type === 'CHALLENGE' ? 'Challenge' : 'Program'}</Tag>
          <Tag>{p.audience === 'EVERYONE' ? 'Everyone' : p.audience === 'WOMEN' ? 'Women' : 'Men'}</Tag>
          {p.published ? <Tag tone="ok">Published</Tag> : <Tag>Draft</Tag>}
          {p.featured && <Tag tone="accent">Featured</Tag>}
        </div>
        <p className="text-xs text-muted">
          {p.level ?? '—'} · {p.durationWeeks} weeks · {p.workoutCount} workouts
        </p>
      </div>
      <Link to={`/programs/${p.id}/workouts`}>
        {p.workoutCount === 0 ? (
          <Button size="sm">
            <Plus className="size-4" aria-hidden /> Add workouts
          </Button>
        ) : (
          <Button size="sm" variant="outline">
            Workouts ({p.workoutCount}) <ArrowRight className="size-4" aria-hidden />
          </Button>
        )}
      </Link>
      <Button size="sm" variant="ghost" onClick={onEdit}>Edit</Button>
      <Button size="sm" variant="ghost" loading={busy} onClick={remove} className="text-bad-ink">Delete</Button>
    </Card>
  );
}

function Tag({ children, tone }: { children: React.ReactNode; tone?: 'ok' | 'accent' }) {
  const cls = tone === 'ok' ? 'bg-ok-soft text-ok-ink' : tone === 'accent' ? 'bg-accent-soft text-ink' : 'bg-surface text-muted';
  return <span className={`rounded-pill px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}

function ProgramForm({ mode, program, onCancel, onDone }: { mode: 'create' | 'edit'; program?: AdminProgram; onCancel: () => void; onDone: (created?: AdminProgram) => void }) {
  const [title, setTitle] = useState(program?.title ?? '');
  const [type, setType] = useState<ProgramType>(program?.type ?? 'PROGRAM');
  const [audience, setAudience] = useState<ProgramAudience>(program?.audience ?? 'EVERYONE');
  const [subtitle, setSubtitle] = useState(program?.subtitle ?? '');
  const [description, setDescription] = useState(program?.description ?? '');
  const [level, setLevel] = useState(program?.level ?? '');
  const [goal, setGoal] = useState(program?.goal ?? '');
  const [targetAreas, setTargetAreas] = useState<string[]>(program?.targetAreas ?? []);
  const [equipment, setEquipment] = useState<string[]>(program?.equipment ?? []);
  const [durationWeeks, setDurationWeeks] = useState(String(program?.durationWeeks ?? 4));
  const [daysPerWeek, setDaysPerWeek] = useState(program?.daysPerWeek ? String(program.daysPerWeek) : '');
  const [coverImage, setCoverImage] = useState(program?.coverImage ?? '');
  const [published, setPublished] = useState(program?.published ?? false);
  const [featured, setFeatured] = useState(program?.featured ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    if (!title.trim()) return setError('Give the program a title.');
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        type,
        audience,
        subtitle: subtitle.trim() || null,
        description: description.trim() || null,
        level: level.trim() || null,
        goal: goal.trim() || null,
        targetAreas,
        equipment,
        durationWeeks: Number(durationWeeks) || 4,
        daysPerWeek: daysPerWeek ? Number(daysPerWeek) : null,
        coverImage: coverImage.trim() || null,
        published,
        featured,
      };
      if (mode === 'create') {
        onDone(await createProgram(body));
      } else {
        await updateProgram(program!.id, body);
        onDone();
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the program.');
      setSaving(false);
    }
  }

  return (
    <Card className="mb-1 flex flex-col gap-3 p-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Full Body Reset" />
        <Input label="Subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
        <Select label="Type" value={type} onChange={(e) => setType(e.target.value as ProgramType)}>
          {TYPES.map((t) => <option key={t} value={t}>{t === 'CHALLENGE' ? 'Challenge' : 'Program'}</option>)}
        </Select>
        <Select label="Audience" value={audience} onChange={(e) => setAudience(e.target.value as ProgramAudience)}>
          {AUDIENCE.map((a) => <option key={a} value={a}>{a === 'EVERYONE' ? 'Everyone' : a === 'WOMEN' ? 'Women' : 'Men'}</option>)}
        </Select>
        <Combobox label="Level" value={level} onChange={setLevel} options={LEVELS} placeholder="Pick a level" />
        <Combobox label="Goal" value={goal} onChange={setGoal} options={GOALS} placeholder="Pick a goal" />
        <Input label="Duration (weeks)" type="number" inputMode="numeric" min={1} max={104} value={durationWeeks} onChange={(e) => setDurationWeeks(e.target.value)} />
        <Select label="Days per week" hint="How many training days the program expects" value={daysPerWeek} onChange={(e) => setDaysPerWeek(e.target.value)}>
          <option value="">Not set</option>
          {DAYS_PER_WEEK.map((d) => <option key={d} value={d}>{d}</option>)}
        </Select>
      </div>
      <MultiSelect label="Target areas" values={targetAreas} onChange={setTargetAreas} options={TARGET_AREAS} placeholder="Add areas this program trains" />
      <MultiSelect label="Equipment" values={equipment} onChange={setEquipment} options={EQUIPMENT} placeholder="Add equipment needed (or none)" />
      <ImageUpload value={coverImage || null} onChange={(url) => setCoverImage(url ?? '')} aspect={16 / 9} />
      <Textarea label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
      <div className="flex flex-col gap-4 rounded-control border border-line bg-elevated p-4">
        <Toggle label="Published" description="Members can find and enroll in this program" checked={published} onChange={setPublished} />
        <Toggle label="Featured" description="Highlight it at the top of the browse screen" checked={featured} onChange={setFeatured} />
      </div>
      {error && <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button size="sm" onClick={submit} loading={saving}>Save</Button>
      </div>
    </Card>
  );
}
